import React from "react";

export interface ViewDefinition {
    name: string;
    component: SyncOrAsyncComponent;
}

interface Views {
    [key: string]: ViewDefinition;
}

type AsyncComponent = () => Promise<ComponentInDefault>;
type SyncComponent = () => ComponentInDefault;
type SyncOrAsyncComponent = AsyncComponent | SyncComponent;

interface ComponentInDefault {
    default: React.ComponentType<any>;
}

export function defineView(props: ViewDefinition): ViewDefinition {
    return {
        ...props,
        component: props.component
    };
}

/**
 * When creating the server-bundle, all async dynamic imports (`const c = import("pathToFile")`)
 * are converted to synchronous requires (`const c = require("pathToFile")`).
 * This is needed, because the server side renderer doesn't wait for promises to
 * be fulfilled.
 * The conversion happens in the "transform-import-to-require" babel plugin.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isAsyncComponent(_component: SyncOrAsyncComponent): _component is AsyncComponent {
    return !__SERVER__;
}

function getViewDefinitions() {
    const req = require.context("~/views", true, /-definition\.(tsx|ts)$/);
    const definitions: ViewDefinition[] = req
        .keys()
        .filter(path => req(path).default)
        .map(path => req(path).default);
    return definitions;
}

function viewDefinitions() {
    const definitions = getViewDefinitions();

    const views: Views = {};
    definitions.forEach(definition => {
        views[definition.name] = definition;
    });

    return views;
}

export function findViewDefinition(name: string) {
    const definitions = viewDefinitions();
    const match = Object.keys(definitions).find(viewName => viewName === name);
    return match ? definitions[match] : undefined;
}

export default viewDefinitions;
